/*  iceberg-ui  */
import { Box, Card, CardBody } from '@auviknetworks/iceberg-ui';
import { Typography } from '@mui/material';

/* Helpers */
import {
    getReportConfigByType,
    getSelectedSubReportOptions
} from 'application/Home/Reports/helpers';

/*  Shared  */
import {
    BusinessOwnerPicker,
    TagsPicker,
    UserPicker
} from 'application/Shared/Components/Pickers';
import AppPicker from 'application/Shared/Components/Pickers/Application';
import GroupPicker from 'application/Shared/Components/Pickers/Group';
import { FontSizes } from 'application/Shared/Constants';
import { getClientName } from 'application/Shared/Helpers/tenant';

/* Hooks */
import { useSession } from 'hooks/redux/Selectors/session';
import { useReportParams } from 'hooks/useReportParams';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

/**
 * Helper functions to determine report capabilities based on allReportsConfig
 */
const shouldShowAppSelection = (reportType: string): boolean => {
    const config = getReportConfigByType(reportType);
    const { setup } = config || {};
    const { multiAppSelectionOptions } = setup || {};
    return (
        config?.setup?.appSelectionOptions !== null ||
        multiAppSelectionOptions?.appSelection === true
    );
};

const shouldShowTagSelection = (reportType: string): boolean => {
    const config = getReportConfigByType(reportType);
    const { setup } = config || {};
    const { multiAppSelectionOptions } = setup || {};
    return (
        config?.setup?.tagSelection === true ||
        multiAppSelectionOptions?.tagSelection === true
    );
};

const shouldShowUserSelection = (reportType: string): boolean => {
    const config = getReportConfigByType(reportType);
    return config?.setup?.userSelectionOptions !== null;
};

const shouldShowBusinessOwnerSelection = (reportType: string): boolean => {
    const config = getReportConfigByType(reportType);
    return config?.setup?.businessOwnerSelection === true;
};

const hasNoAppsSelected = (
    reportType: string,
    applicationSelection?: string[]
): boolean => {
    const config = getReportConfigByType(reportType);
    const hasAppSelection = config?.setup?.appSelectionOptions !== null;

    if (!hasAppSelection) return false;

    // Check if the app selection indicates no apps are selected
    // This happens when applicationSelection is ['0'] (all apps) or empty
    return !applicationSelection || applicationSelection[0] === '0';
};

const RenderReportDetails = ({
    title,
    value
}: {
    title: string;
    value: string | JSX.Element;
}) => (
    <>
        <Typography
            sx={{
                color: 'text.secondary',
                fontSize: FontSizes[1],
                fontWeight: 600
            }}
        >
            {title}
        </Typography>
        <Box
            sx={{
                color: 'text.secondary',
                fontSize: FontSizes[1]
            }}
        >
            {value}
        </Box>
    </>
);

const SubReportOptions = ({
    subReportOptions
}: {
    subReportOptions: Array<{ options: string[]; type: string }>;
}) =>
    subReportOptions.map((subReport, index) => (
        <Box
            key={subReport.type}
            sx={{
                marginBottom: index < subReportOptions.length - 1 ? '8px' : 0
            }}
        >
            <Typography variant="span" fontWeight={600}>
                {`${subReport.type}: `}
            </Typography>
            {subReport.options.join(' / ')}
        </Box>
    ));

export const ReportDetails = () => {
    const { t } = useTranslation();
    const session = useSession();
    const { partnerId, reportType } = useParams();

    const parsedReportParams = useReportParams(reportType);

    const {
        applicationSelection,
        businessOwnerSelection,
        discoveryOverviewMatrixTags,
        groupSelection,
        period,
        platformTenantId,
        reportSelections,
        tagSelection,
        type,
        userOrGroupSelection,
        userSelection
    } = parsedReportParams;

    const showAppPicker = shouldShowAppSelection(type);
    const showUserSelection = shouldShowUserSelection(type);
    const showBusinessOwnerSelection = shouldShowBusinessOwnerSelection(type);
    const reportsWithTagSelection = shouldShowTagSelection(type);
    const noAppsSelected = hasNoAppsSelected(type, applicationSelection);

    const tagsSelected =
        (tagSelection && tagSelection?.length > 0 && tagSelection[0] !== '0') ||
        discoveryOverviewMatrixTags[0] !== '0';

    const tagValues =
        discoveryOverviewMatrixTags[0] !== '0'
            ? discoveryOverviewMatrixTags
            : tagSelection;

    const clientName = getClientName({
        clientId: platformTenantId || partnerId,
        session
    });

    const subReportOptions = getSelectedSubReportOptions({
        reportSelections,
        t,
        type
    });

    const showSubReportOptionsDetails = !!subReportOptions?.length;
    const showReportPeriod = period !== 'period';

    return (
        <Card>
            <CardBody sx={{ p: 3 }}>
                <Typography
                    sx={{
                        color: 'text.secondary',
                        fontSize: FontSizes[2],
                        fontWeight: 600,
                        marginBottom: '16px'
                    }}
                >
                    {t('Reports.reportDetails')}
                </Typography>
                <hr />
                <Box
                    sx={{
                        display: 'grid',
                        gap: 2,
                        gridTemplateColumns: '1fr 1fr'
                    }}
                >
                    {showReportPeriod && (
                        <RenderReportDetails
                            title={t('Common.period')}
                            value={t(`Reports.period.${period}`)}
                        />
                    )}
                    <RenderReportDetails
                        title={t('Reports.tenantName')}
                        value={clientName}
                    />
                    <RenderReportDetails
                        title={t('Reports.reportType')}
                        value={t(`Reports.Title.${type}.report`)}
                    />

                    {showSubReportOptionsDetails && (
                        <RenderReportDetails
                            title={t('Reports.subReportOptions')}
                            value={
                                <SubReportOptions
                                    subReportOptions={subReportOptions}
                                />
                            }
                        />
                    )}
                    {showAppPicker && (
                        <RenderReportDetails
                            title={t('Reports.applications.selected')}
                            value={
                                noAppsSelected ? (
                                    t(
                                        'Reports.applications.noApplicationsSelected'
                                    )
                                ) : (
                                    <AppPicker
                                        allApplications
                                        allValue={t(
                                            'Reports.applications.allApplicationsSelected'
                                        )}
                                        allValueId="0"
                                        hideManagedBadge
                                        multiple
                                        value={applicationSelection}
                                        visualOnly
                                    />
                                )
                            }
                        />
                    )}
                    {showUserSelection && userOrGroupSelection === 'users' && (
                        <RenderReportDetails
                            title={t('Reports.users.selected')}
                            value={
                                <UserPicker
                                    multiple
                                    allValue={t(
                                        'Reports.users.allUsersSelected'
                                    )}
                                    allValueId="0"
                                    visualOnly
                                    value={userSelection}
                                />
                            }
                        />
                    )}
                    {showUserSelection && userOrGroupSelection === 'groups' && (
                        <RenderReportDetails
                            title={t('Reports.groups.selected')}
                            value={
                                <GroupPicker
                                    multiple
                                    allValue={t(
                                        'Reports.groups.allGroupsSelected'
                                    )}
                                    allValueId="0"
                                    visualOnly
                                    value={groupSelection}
                                />
                            }
                        />
                    )}
                    {showBusinessOwnerSelection && (
                        <RenderReportDetails
                            title={t('Reports.businessOwner')}
                            value={
                                <BusinessOwnerPicker
                                    visualOnly
                                    value={businessOwnerSelection}
                                />
                            }
                        />
                    )}
                    {reportsWithTagSelection && (
                        <RenderReportDetails
                            title={t('Reports.tags.selected')}
                            value={
                                tagsSelected ? (
                                    <TagsPicker
                                        multiple
                                        allValue={t(
                                            'Reports.tags.allTagsSelected'
                                        )}
                                        unselectedOption={t(
                                            'Reports.tags.noTagsSelected'
                                        )}
                                        visualOnly
                                        value={tagValues}
                                    />
                                ) : (
                                    t('Reports.tags.noTagsSelected')
                                )
                            }
                        />
                    )}
                </Box>
            </CardBody>
        </Card>
    );
};
